// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import XngZone from "@stanion/App/Components/Common/XngZone";
import xLoadAllEvents from "@stanion/App/Handlers/AllEvents/AllEventsHandler";
import updateSearchFields from "@stanion/App/Handlers/AllEvents/UpdateSearchFields";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { EventListPageContext, getAllEventsDataView } from "@stanion/App/Store/Selectors/xAllEventsRequestSelector";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        allEventsDataView: getAllEventsDataView(state, state.xAllEventsPage.getAllEventsParameter),
        getAllEventsParameter: state.xAllEventsPage.getAllEventsParameter,
    };
};

const mapDispatchToProps = {
    xLoadAllEvents,
    updateSearchFields,
};

export interface AllEventsPageStyles {
    leftSection?: GridItemProps;
    rightSection?: GridItemProps;
}

const allEventsPageStyles: AllEventsPageStyles = {
    leftSection: {
        width: [12, 12, 7, 7, 7],
    },
    rightSection: {
        width: [12, 12, 5, 5, 5],
    },
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = allEventsPageStyles;
const AllEventsPage: React.FunctionComponent<Props> = ({
    id,
    allEventsDataView,
    getAllEventsParameter,
    xLoadAllEvents,
    updateSearchFields,
}: Props) => {
    React.useEffect(() => {
        if (allEventsDataView.isLoading) {
            return;
        }
        if (!allEventsDataView.value) {
            xLoadAllEvents(getAllEventsParameter);
        } else if (allEventsDataView.value?.length === 0 && getAllEventsParameter.page) {
            updateSearchFields({ page: getAllEventsParameter.page - 1 });
        }
    });

    return (
        <Page data-test-selector="AllEventsPage">
            <EventListPageContext.Provider value={allEventsDataView}>
                <GridContainer gap={20}>
                    <GridItem width={12}>
                        <XngZone contentId={id} zoneName="TopSection" />
                    </GridItem>
                    <GridItem width={12}>
                        <GridContainer gap={20}>
                            <GridItem {...styles.leftSection}>
                                <XngZone contentId={id} zoneName="LeftSection" />
                            </GridItem>
                            <GridItem {...styles.rightSection}>
                                <XngZone contentId={id} zoneName="RightSection" />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </EventListPageContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AllEventsPage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
